/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { CluMigrationSteps } from 'src/luis/verticals/cluMigrationModal/CluMigrationModal';

export type CluMigrationStepsNavigatorProps = {
    activeStep: number;
    horizontal?: boolean;
    onActiveStepChange: (newStep: number) => void;
};

const messages = defineMessages({
    migrationOverview: {
        id: 'cluMigration.cluMigrationModalStepsNavigator.migrationOverview',
        defaultMessage: 'Migration overview'
    },
    selectLanguageResource: {
        id: 'cluMigration.cluMigrationModalStepsNavigator.selectLanguageResource',
        defaultMessage: 'Select Language resource'
    },
    selectLuisApplications: {
        id: 'cluMigration.cluMigrationModalStepsNavigator.selectLuisApplications',
        defaultMessage: 'Select LUIS applications'
    },
    reviewAndFinish: {
        id: 'cluMigration.cluMigrationModalStepsNavigator.reviewAndFinish',
        defaultMessage: 'Review and finish'
    }
});

/**
 * Displays the side navigator in the clu migration modal.
 */
export const CluMigrationModalStepsNavigator = (props: CluMigrationStepsNavigatorProps) => {
    const { activeStep, onActiveStepChange, horizontal } = props;

    const { localize } = useLocalization();

    const getStepStatus = (stepIndex: CluMigrationSteps): 'none' | 'completed' | 'active' => {
        if (stepIndex > activeStep) {
            return 'none';
        }
        if (stepIndex === activeStep) {
            return 'active';
        }

        return 'completed';
    };

    const steps: LuisStepperStepType[] = [
        {
            id: 'migrationOverview',
            title: localize(messages.migrationOverview),
            disabled: activeStep < CluMigrationSteps.MigrationOverview,
            state: getStepStatus(CluMigrationSteps.MigrationOverview)
        },
        {
            id: 'selectLanguageResource',
            title: localize(messages.selectLanguageResource),
            disabled: activeStep < CluMigrationSteps.SelectLanguageResource,
            state: getStepStatus(CluMigrationSteps.SelectLanguageResource)
        },
        {
            id: 'selectLuisApplications',
            title: localize(messages.selectLuisApplications),
            disabled: activeStep < CluMigrationSteps.SelectLuisApplications,
            state: getStepStatus(CluMigrationSteps.SelectLuisApplications)
        },
        {
            id: 'reviewAndFinish',
            title: localize(messages.reviewAndFinish),
            disabled: activeStep < CluMigrationSteps.ReviewAndFinish,
            state: getStepStatus(CluMigrationSteps.ReviewAndFinish)
        }
    ];

    const onStepClick = (stepKey: string) => onActiveStepChange(steps.findIndex(step => step.id === stepKey));

    return <LuisStepper horizontal={horizontal} onStepClick={onStepClick} steps={steps} />;
};
